import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import { StatusCode } from "api/protocols";
import {
  GetEmailTemplate,
  PutEmailTemplate,
  DeleteEmailTemplate,
  TEmailTemplateType,
  IEmailTemplate,
  GetEmailTemplateVariables,
  IEmailTemplateVariable,
  PostTestEmail,
  PostPreviewEmail,
} from "api/rpc/2024-04/facilityAdmin/facility/emailTemplate";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import TextEditor from "components/textEditor/textEditor";
import Popup from "components/popup/Popup";
import { Select } from "components/select";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import "./emailTemplates.scss";
import { Badge } from "components/badge/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faClock, faDollar, faLock, faTicket, faUserGroup } from "@fortawesome/pro-regular-svg-icons";
import { CustomIcon } from "components/customIcon/CustomIcon";
import axios, { CancelToken } from "axios";
import useModal from "hooks/modals/useModal";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import Spin from "components/spin/spin";

interface IEditEmailTemplateState {
  title: string;
  type: TEmailTemplateType;
  subject: string;
  content: string;
  automated: boolean;
  deletePopupVisible: boolean;
}

const AUTOMATED_EMAILS = ["tee_sheet_booking_confirmation", "tee_sheet_booking_cancellation", "reservation_booking_confirmation", "reservation_booking_cancellation", "welcome_email", "no_show_email", "birthday_request_email"];
const PROFILE_EMAILS = ["welcome_email", "birthday_request_email"];

export default function EmailTemplateEdit() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { templateId } = useParams<{ templateId: string }>();
  const [automatedTemplates, setAutomatedTemplates] = useState<Array<IEmailTemplate>>([]);
  const [emailVariables, setEmailVariables] = useState<Array<IEmailTemplateVariable>>([]);
  const [previewEmailHTML, setPreviewEmailHTML] = useState<string>(undefined);
  const { state: testEmailPopup, updateModal: updateTestEmailPopup, closeModal: closeTestEmailPopup } = useModal<{ email: string }>({ email: "" });

  const [editTemplateState, setEditTemplateState] = useState<IEditEmailTemplateState>({
    title: "",
    type: undefined,
    subject: "",
    content: "",
    deletePopupVisible: false,
    automated: false,
  });

  const templateTypes = [
    { label: t("Tee Sheet"), value: "tee_sheet" },
    {
      label: t("League Announcement"),
      value: "league_announcement",
    },
    {
      label: t("Tee Time Booking Confirmation"),
      value: "tee_sheet_booking_confirmation",
    },
    {
      label: t("Tee Time Booking Cancellation"),
      value: "tee_sheet_booking_cancellation",
    },
    {
      label: t("Reservation Booking Confirmation"),
      value: "reservation_booking_confirmation",
    },
    {
      label: t("Reservation Booking Cancellation"),
      value: "reservation_booking_cancellation",
    },
    {
      label: t("Welcome Email"),
      value: "welcome_email",
    },
    {
      label: t("No Show Email"),
      value: "no_show_email",
    },
    {
      label: t("Birthday Request Email"),
      value: "birthday_request_email",
    },
  ];

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTemplate(source.token);
    return () => source.cancel();
  }, []);

  useEffect(() => {
    if (!editTemplateState?.content) {
      setPreviewEmailHTML(null);
      return;
    }

    const timeout = setTimeout(() => {
      void previewEmail();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [editTemplateState.content, editTemplateState?.type]);

  async function loadTemplate(cancelToken: CancelToken) {
    dispatch(enqueue());
    const [templateRes, automatedTemplatesRes, variablesRes] = await Promise.all([GetEmailTemplate({ id: Number(templateId) }, false, cancelToken), GetEmailTemplate({ automated: true }, false, cancelToken), GetEmailTemplateVariables(false, cancelToken)]);
    dispatch(dequeue());
    if (templateRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error loading email template")));
      return;
    }

    if (automatedTemplatesRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error loading automated email templates")));
      return;
    }

    if (variablesRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error loading template variables")));
      return;
    }

    const automatedTemplates = Array.isArray(automatedTemplatesRes?.data) ? automatedTemplatesRes?.data?.filter(template => template?.id !== (Number(templateId))) : [];

    ReactDOM.unstable_batchedUpdates(() => {
      setEditTemplateState(prev => ({
        ...prev,
        title: templateRes.data[0].title,
        type: templateRes.data[0].type,
        subject: templateRes.data[0].subject,
        content: templateRes.data[0].content,
        automated: templateRes?.data[0]?.automated,
      }));
      setAutomatedTemplates(automatedTemplates);
      setEmailVariables(variablesRes?.data);
    });
  }

  async function updateTemplate() {
    const templateRes = await PutEmailTemplate(
      {
        id: Number(templateId),
        title: editTemplateState.title,
        type: editTemplateState.type,
        subject: editTemplateState.subject,
        content: editTemplateState.content,
        automated: AUTOMATED_EMAILS.includes(editTemplateState?.type),
      },
      true,
    );

    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(typeof templateRes?.data === "string" ? templateRes?.data : t("Error updating email template")));
      return;
    }

    dispatch(showSuccess(t("Email template updated successfully")));
    history.push("/admin/settings/email-template");
  }

  async function deleteTemplate() {
    const templateRes = await DeleteEmailTemplate(
      {
        id: Number(templateId),
      },
      true,
    );

    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting email template")));
      return;
    }

    dispatch(showSuccess(t("Email template deleted successfully")));
    history.push("/admin/settings/email-template");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setEditTemplateState(prev => ({ ...prev, [id]: value }));
  }

  async function sendTestEmail() {
    if (!testEmailPopup?.email) {
      dispatch(showError(t("Email required")));
      return;
    }

    const res = await PostTestEmail({ content: editTemplateState?.content, subject: editTemplateState?.subject, email: testEmailPopup?.email, template_type: editTemplateState?.type }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error sending test email")));
      return;
    }
    dispatch(showSuccess(t("Successfully sent test email")));
    void closeTestEmailPopup();
  }

  async function previewEmail() {
    //loader
    if (previewEmailHTML !== undefined) {
      setPreviewEmailHTML(undefined);
    }
    const res = await PostPreviewEmail({ content: editTemplateState?.content, subject: editTemplateState?.subject, template_type: editTemplateState?.type }, false);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error previewing email")));
      setPreviewEmailHTML("");
      return;
    }
    setPreviewEmailHTML(res?.data as string);
  }

  const primaryAction = {
    content: t("Update"),
    action: updateTemplate,
  };

  const secondaryActions = [
    {
      content: t("Delete"),
      action: () => setEditTemplateState(prev => ({ ...prev, deletePopupVisible: true })),
    },
    {
      content: t("Send Test Email"),
      action: () => updateTestEmailPopup({ isOpen: true }),
    },
  ];

  return (
    <Page
      title={t("Edit Email Template")}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Email templates"),
          url: "/admin/settings/email-template",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={editTemplateState.title}
                label={t("Title")}
                id="title"
                onChange={handleInputChange}
              />
              <Input
                value={editTemplateState.subject}
                label={t("Subject")}
                id="subject"
                onChange={handleInputChange}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                defaultValue={editTemplateState.type}
                label={t("Type")}
                onChange={value => setEditTemplateState(prev => ({ ...prev, type: value }))}
              >
                {templateTypes?.filter(type => !automatedTemplates?.some(template => template?.type === type?.value))?.map(type => (
                  <Select.Option key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      {type.label}
                      {AUTOMATED_EMAILS.includes(type.value) ? <Badge type="gray" size="small">{t("Automated")}</Badge> : null}
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group alignStretch>
              <TextEditor
                markdownText={editTemplateState.content ?? ""}
                markdownTextOnChange={(newContent: string) =>
                  setEditTemplateState(prev => ({ ...prev, content: newContent }))
                }
                label={t("Content")}
                variables={AUTOMATED_EMAILS.includes(editTemplateState?.type) ? emailVariables : undefined}
              />
              {previewEmailHTML === undefined ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex justify-center items-center flex-column">
                    <div style={{ width: "70px" }}>
                      <Spin />
                    </div>
                    <p>Loading preview...</p>
                  </div>
                </div>
              ) : previewEmailHTML === null ? (
                <div>
                  <span className="ui-email-template-preview-label">{t("Preview")}</span>
                </div>
              ) : (
                <div style={{ height: "100%" }}>
                  <span className="ui-email-template-preview-label">{t("Preview")}</span>
                  <div className="ui-email-template-preview-email">
                    <iframe srcDoc={
                      //Don't allow links to be clickable in the preview
                      `
                    <style>
                      a {
                        pointer-events: none;
                        cursor: default;
                      }
                    </style>
                    ${previewEmailHTML}
                    `} style={{ width: "70%" }} />
                  </div>
                </div>
              )}
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>

        {AUTOMATED_EMAILS.includes(editTemplateState?.type) && !PROFILE_EMAILS.includes(editTemplateState?.type) ? (
          <div className="ui-email-template-booking-details">
            <div className="ui-email-template-booking-details-header">
              <p>{t("Booking Details")}</p>
              <Badge size="small" type="gray"><FontAwesomeIcon icon={faLock} className="mr-1" /> {t("Locked")}</Badge>
            </div>
            <div className="ui-email-template-booking-details-content">
              <div className="ui-email-template-booking-details-content-details">
                <FontAwesomeIcon icon={faTicket} className="ui-email-template-booking-details-content-details-icon" />
                <p>{t("Booking Code")}</p>
              </div>
              <div className="ui-email-template-booking-details-content-divider"></div>
              <div className="ui-email-template-booking-details-content-details">
                <FontAwesomeIcon icon={faCalendar} className="ui-email-template-booking-details-content-details-icon" />
                <p>{t("Date")}</p>
              </div>
              <div className="ui-email-template-booking-details-content-divider"></div>
              <div className="ui-email-template-booking-details-content-details">
                <FontAwesomeIcon icon={faClock} className="ui-email-template-booking-details-content-details-icon" />
                <p>{t("Time")}</p>
              </div>
              <div className="ui-email-template-booking-details-content-divider"></div>
              <div className="ui-email-template-booking-details-content-details">
                <FontAwesomeIcon icon={faUserGroup} className="ui-email-template-booking-details-content-details-icon" />
                <p>{t("Players")}</p>
              </div>
              <div className="ui-email-template-booking-details-content-divider"></div>
              <div className="ui-email-template-booking-details-content-details">
                <CustomIcon src="/public/images/powercart2-01.svg" size="medium" colour="gray" />
                <p>{t("Cart")}</p>
              </div>
              <div className="ui-email-template-booking-details-content-divider"></div>
              <div className="ui-email-template-booking-details-content-details">
                <FontAwesomeIcon icon={faDollar} className="ui-email-template-booking-details-content-details-icon" />
                <p>{t("Price")}</p>
              </div>
            </div>
          </div>
        ) : null}
      </Card>

      <Popup
        open={editTemplateState.deletePopupVisible}
        type="warning"
        title={t("Delete Email Template?")}
        description={t("Are you sure you want to delete this email template? This cannot be undone.")}
        onOk={deleteTemplate}
        onCancel={() => setEditTemplateState(prev => ({ ...prev, deletePopupVisible: false }))}
        okText={t("Delete")}
      />

      <Portal isMounted={testEmailPopup?.isOpen}>
        <Sheet
          title={t("Send Test Email")}
          supportText={t("Enter an email address to receive a test email")}
          onCancel={closeTestEmailPopup}
          onOk={sendTestEmail}
          closable
          size="small"
          open={testEmailPopup?.isOpen}
          okText={t("Send")}
          okDisabled={!testEmailPopup?.email}
        >
          <div className="p-1">
            <Input labelHidden placeholder={t("Enter email address")} onChange={e => updateTestEmailPopup({ email: e.target.value })} value={testEmailPopup?.email} />
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}
